import React, { useEffect } from "react";
import { HashtagNode } from "@lexical/hashtag";

import {
  InitialConfigType,
  LexicalComposer,
} from "@lexical/react/LexicalComposer";

import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import {
  HistoryPlugin,
  HistoryState,
} from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { commonTextExport, commonTextImport } from "./editor-state-conversion";
import { useEventCommands } from "./use-event-commands";
import { AtMentionNode } from "./at-mention/at-mention-node";
import {
  $getRoot,
  EditorState,
  ElementNode,
  HISTORIC_TAG,
  Klass,
  LexicalEditor,
  LexicalNode,
  LexicalNodeReplacement,
} from "lexical";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { cn } from "../../lib/utils";

export interface EditorProps {
  onCaptionChange?: (caption: string, state: EditorState) => void;
  plugins?: React.ReactNode;
  initInput?: InputContext;
  input?: Omit<TextInputContext, "type">;
  readonly?: boolean;
  disabled?: boolean;
  theme?: { [key: string]: string | undefined | object };
  className?: string;
  onEditorContextReady?: (params: { editor: LexicalEditor }) => void;
  historyState?: HistoryState;
  initNodes?: (Klass<LexicalNode> | LexicalNodeReplacement)[];
  onEnterSubmit?: () => void;
  placeholder?: string;
  placeholderClassName?: string;
  htmlRef?: React.RefObject<HTMLDivElement>;
  contentHtmlRef?: React.RefObject<HTMLDivElement>;
  style?: React.CSSProperties;
  initEditorState?: EditorState | null;
  disableHistory?: boolean;
  contentEditableClassName?: string;
}

const theme = {};

function onError(error: Error) {
  console.error(error);
}

export const SkedEditor = (props: EditorProps) => {
  const initialConfig: InitialConfigType = {
    namespace: "MyEditor",
    theme: props.theme || theme,
    onError,
    nodes: [HashtagNode, AtMentionNode, ...(props.initNodes || [])],
    editorState: props.initEditorState,
  };
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Editor {...props} />
    </LexicalComposer>
  );
};

interface TextInputContext {
  type: "text";
  text: string;
  textImportProcess?: (text: string) => ElementNode | undefined;
}

interface StateInputContext {
  type: "state";
  editorState: EditorState;
}

export type InputContext = TextInputContext | StateInputContext;

export const skedEditorQueryClient = new QueryClient();
export const Editor = ({
  onCaptionChange,
  plugins,
  initInput,
  readonly,
  disabled,
  className,
  onEditorContextReady,
  historyState,
  input,
  onEnterSubmit,
  placeholder,
  placeholderClassName,
  htmlRef,
  contentHtmlRef,
  style,
  disableHistory,
  contentEditableClassName,
}: EditorProps) => {
  const [editor] = useLexicalComposerContext();
  const [initHistoryState, setInitHistoryState] =
    React.useState<HistoryState>();
  const [initialized, setInitialized] = React.useState(false);
  const textRef = React.useRef<string>();
  useEffect(() => {
    editor.setEditable(!disabled && !readonly);
  }, [editor, readonly, disabled]);

  useEffect(() => {
    if (editor) {
      onEditorContextReady?.({ editor });
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    const onUpdate = () => {
      if (historyState) {
        if (historyState.current) {
          historyState.current.editor = editor;
        }
        for (const state of historyState.redoStack) {
          state.editor = editor;
        }
        for (const state of historyState.undoStack) {
          state.editor = editor;
        }
        setInitHistoryState(historyState);
      }
    };
    const tag = historyState ? HISTORIC_TAG : undefined;
    if (initInput?.type === "state") {
      editor.update(
        () => {
          editor.setEditorState(initInput.editorState, { tag });
        },
        { onUpdate }
      );
    } else if (initInput?.type === "text") {
      editor.update(
        () => {
          const importFunc = initInput.textImportProcess || commonTextImport;
          replaceRoot(importFunc(initInput.text));
        },
        { onUpdate }
      );
    } else {
      onUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (input && textRef.current !== input.text) {
      textRef.current = input.text;
      editor.update(() => {
        const importFunc = input.textImportProcess || commonTextImport;
        replaceRoot(importFunc(input.text));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input?.text]);

  const { isFocused } = useEventCommands(editor, { onEnterSubmit });

  return (
    <QueryClientProvider client={skedEditorQueryClient}>
      <div
        className={cn(
          `flex relative [&_p]:text-inherit [&_p]:text-[length:inherit] 
          [&_p]:leading-[inherit] [&_p]:font-[weight:inherit] [&_p]:m-[inherit] 
          data-focused:border-blue-400 transition-colors duration-200
          ${disabled ? "cursor-not-allowed bg-gray-100" : ""}`,
          className
        )}
        data-focused={isFocused ? "true" : undefined}
        ref={htmlRef}
        style={style}
        data-disabled={disabled ? "true" : undefined}
      >
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              className={cn(
                `sked-editor-content-editable outline-none flex-1 p-0 overflow-auto whitespace-break-spaces`,
                contentEditableClassName
              )}
              ref={contentHtmlRef}
              disabled={disabled}
              aria-placeholder={placeholder || ""}
              placeholder={
                placeholder ? (
                  <span
                    className={cn(
                      ` text-gray-400 absolute left-0 top-0 pointer-events-none`,
                      placeholderClassName
                    )}
                  >
                    {placeholder}
                  </span>
                ) : (
                  <span />
                )
              }
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>
      <>
        {!disableHistory && (
          <HistoryPlugin externalHistoryState={initHistoryState} />
        )}
        {plugins}
        <OnChangePlugin
          onChange={(state, editor) => {
            if (!readonly && !disabled && onCaptionChange && initialized) {
              editor.read(() => {
                const textExported = commonTextExport();
                if (textRef.current !== textExported) {
                  textRef.current = textExported;
                  onCaptionChange(textExported, state);
                }
              });
            }
          }}
        />
      </>
    </QueryClientProvider>
  );
};

function replaceRoot(node: ElementNode | undefined) {
  if (!node) {
    return;
  }

  const root = $getRoot();
  root.clear();
  root.append(node);
}
